import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, Plus, Filter, X, Edit2,
  ArrowUpRight, ArrowDownRight, Calendar, Receipt
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import SidebarLayout from '../components/SidebarLayout';

import API from '../utils/api';

function formatCurrency(n) {
  if (n == null) return '₹0';
  return '₹' + Number(n).toLocaleString('en-IN');
}

function getFYDates() {
  const now = new Date();
  const fyStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return { start: `${fyStart}-04-01`, end: `${fyStart + 1}-03-31` };
}

export default function FinancePage() {
  const fy = getFYDates();
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(null); // 'income' | 'expense' | null
  const [editEntry, setEditEntry] = useState(null);
  const [tab, setTab] = useState('overview'); // overview | income | expense
  const [fCategory, setFCategory] = useState('');
  const [fClient, setFClient] = useState('');
  const [accountFilter, setAccountFilter] = useState('consolidated'); // consolidated | Personal | Company
  const [startDate, setStartDate] = useState(fy.start);
  const [endDate, setEndDate] = useState(fy.end);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const entryParams = { start_date: startDate, end_date: endDate };
      if (tab === 'income') entryParams.entry_type = 'income';
      if (tab === 'expense') entryParams.entry_type = 'expense';
      if (fCategory && fCategory !== 'all') entryParams.category = fCategory;
      if (fClient && fClient !== 'all') entryParams.client_id = fClient;
      if (accountFilter && accountFilter !== 'consolidated') entryParams.account_type = accountFilter;
      const statsParams = { start_date: startDate, end_date: endDate };
      if (accountFilter && accountFilter !== 'consolidated') statsParams.account_type = accountFilter;

      const [statsRes, entriesRes, incCatRes, expCatRes, clientRes] = await Promise.all([
        axios.get(`${API}/finance/stats`, { withCredentials: true, params: statsParams }),
        axios.get(`${API}/finance`, { withCredentials: true, params: entryParams }),
        axios.get(`${API}/finance/categories?entry_type=income`, { withCredentials: true }),
        axios.get(`${API}/finance/categories?entry_type=expense`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setEntries(entriesRes.data);
      setIncomeCategories(incCatRes.data);
      setExpenseCategories(expCatRes.data);
      setClients(clientRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [startDate, endDate, tab, fCategory, fClient, accountFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (finId) => {
    try {
      await axios.delete(`${API}/finance/${finId}`, { withCredentials: true });
      toast.success('Entry deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading && !stats) {
    return <SidebarLayout title="Finance" subtitle="Finance Tracker"><div className="flex items-center justify-center py-20"><div className="text-white">Loading...</div></div></SidebarLayout>;
  }

  return (
    <SidebarLayout title="Finance" subtitle="Finance Tracker">
      {/* KPI Cards */}
      <div className="kpi-scroll md:grid-cols-4 gap-4 mb-6" data-testid="finance-kpis">
        <div className="kpi-card border-l-4 border-emerald-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Total Income</span>
          <div className="font-heading text-2xl font-black text-emerald-400 mt-1">{formatCurrency(stats?.total_income)}</div>
        </div>
        <div className="kpi-card border-l-4 border-rose-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Total Expenses</span>
          <div className="font-heading text-2xl font-black text-rose-400 mt-1">{formatCurrency(stats?.total_expenses)}</div>
        </div>
        <div className="kpi-card border-l-4 border-cyan-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Net Profit</span>
          <div className={`font-heading text-2xl font-black mt-1 ${(stats?.net_profit || 0) >= 0 ? 'text-cyan-400' : 'text-rose-400'}`}>{formatCurrency(stats?.net_profit)}</div>
        </div>
        <div className="kpi-card border-l-4 border-amber-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Outstanding</span>
          <div className="font-heading text-2xl font-black text-amber-400 mt-1">{formatCurrency(stats?.outstanding)}</div>
        </div>
      </div>

      {/* Date Range + Account Filter */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Calendar size={14} className="text-slate-400" />
        <span className="text-xs text-slate-400 font-semibold uppercase tracking-widest">Period:</span>
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-testid="fin-start-date" />
        <span className="text-slate-500 text-xs">to</span>
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-testid="fin-end-date" />
        <button onClick={() => { setStartDate(fy.start); setEndDate(fy.end); }} className="text-[10px] px-2 py-1 rounded bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30 transition-colors" data-testid="fin-reset-fy">Reset to FY</button>
        <div className="flex-1" />
        <div className="inline-flex rounded-lg border border-white/10 bg-black/40 overflow-hidden" data-testid="fin-account-toggle">
          {[
            { v: 'consolidated', label: 'Consolidated' },
            { v: 'Company', label: 'Company' },
            { v: 'Personal', label: 'Personal' },
          ].map(opt => (
            <button key={opt.v} onClick={() => setAccountFilter(opt.v)}
              className={`px-3 py-1.5 text-[11px] font-semibold transition-colors ${accountFilter === opt.v ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid={`fin-account-${opt.v.toLowerCase()}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Account split bar (only when consolidated) */}
      {accountFilter === 'consolidated' && stats?.by_account && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4" data-testid="fin-account-split">
          {['Company', 'Personal'].map(acc => {
            const d = stats.by_account[acc] || { income: 0, expense: 0 };
            const net = (d.income || 0) - (d.expense || 0);
            return (
              <div key={acc} className="glass-card p-3 flex items-center justify-between" data-testid={`fin-account-card-${acc.toLowerCase()}`}>
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-8 rounded ${acc === 'Company' ? 'bg-indigo-500' : 'bg-purple-500'}`} />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{acc}</div>
                    <div className="text-xs text-slate-300">
                      <span className="text-emerald-400 font-mono">{formatCurrency(d.income)}</span>
                      <span className="text-slate-500 mx-1">−</span>
                      <span className="text-rose-400 font-mono">{formatCurrency(d.expense)}</span>
                    </div>
                  </div>
                </div>
                <div className={`text-xl font-heading font-black ${net >= 0 ? 'text-cyan-400' : 'text-rose-400'}`}>{formatCurrency(net)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4" data-testid="finance-tabs">
        {['overview', 'income', 'expense'].map(t => (
          <button key={t} onClick={() => { setTab(t); setFCategory(''); setFClient(''); }}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${tab === t ? 'bg-white/10 text-white' : 'text-slate-500 hover:text-white'}`}
            data-testid={`fin-tab-${t}`}
          >{t === 'overview' ? 'Overview' : t === 'income' ? 'Income' : 'Expenses'}</button>
        ))}
        <div className="flex-1" />
        <Button onClick={() => setShowCreate('income')} variant="outline" className="h-8 text-xs bg-emerald-600/10 border-emerald-500/20 text-emerald-400" data-testid="add-income-btn">
          <ArrowUpRight size={12} className="mr-1" /> Add Income
        </Button>
        <Button onClick={() => setShowCreate('expense')} variant="outline" className="h-8 text-xs bg-rose-600/10 border-rose-500/20 text-rose-400 ml-2" data-testid="add-expense-btn">
          <ArrowDownRight size={12} className="mr-1" /> Add Expense
        </Button>
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && stats && (
        <div className="space-y-4 animate-fadeIn">
          {/* Monthly Breakdown */}
          {Object.keys(stats.monthly || {}).length > 0 && (
            <div className="glass-card overflow-hidden" data-testid="monthly-breakdown">
              <div className="px-4 py-3 border-b border-white/5">
                <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Monthly Breakdown</span>
              </div>
              <table className="data-table">
                <thead><tr><th>Month</th><th>Income</th><th>Expenses</th><th>Net</th></tr></thead>
                <tbody>
                  {Object.entries(stats.monthly).map(([month, data]) => (
                    <tr key={month}>
                      <td className="text-white text-sm">{month}</td>
                      <td className="text-emerald-400 font-mono text-sm">{formatCurrency(data.income)}</td>
                      <td className="text-rose-400 font-mono text-sm">{formatCurrency(data.expense)}</td>
                      <td className={`font-mono text-sm font-bold ${(data.income - data.expense) >= 0 ? 'text-cyan-400' : 'text-rose-400'}`}>{formatCurrency(data.income - data.expense)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Client Income */}
            {Object.keys(stats.client_income || {}).length > 0 && (
              <div className="glass-card p-4" data-testid="client-income">
                <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Client-wise Income</h3>
                <div className="space-y-1.5">
                  {Object.entries(stats.client_income).sort((a, b) => b[1] - a[1]).map(([client, amt]) => (
                    <div key={client} className="flex justify-between text-xs">
                      <span className="text-slate-300">{client}</span>
                      <span className="text-emerald-400 font-bold">{formatCurrency(amt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Category Splits */}
            <div className="space-y-4">
              {Object.keys(stats.income_by_category || {}).length > 0 && (
                <div className="glass-card p-4" data-testid="income-by-category">
                  <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Income by Category</h3>
                  <div className="space-y-1.5">
                    {Object.entries(stats.income_by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                      <div key={cat} className="flex justify-between text-xs">
                        <span className="text-slate-300">{cat}</span>
                        <span className="text-emerald-400 font-bold">{formatCurrency(amt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Object.keys(stats.expense_by_category || {}).length > 0 && (
                <div className="glass-card p-4" data-testid="expense-by-category">
                  <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Expenses by Category</h3>
                  <div className="space-y-1.5">
                    {Object.entries(stats.expense_by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                      <div key={cat} className="flex justify-between text-xs">
                        <span className="text-slate-300">{cat}</span>
                        <span className="text-rose-400 font-bold">{formatCurrency(amt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Income / Expense Tabs */}
      {(tab === 'income' || tab === 'expense') && (
        <div className="animate-fadeIn">
          {/* Filters */}
          <div className="flex gap-3 mb-4 flex-wrap">
            <Select value={fCategory} onValueChange={setFCategory}>
              <SelectTrigger className="bg-black/40 border-white/10 text-slate-300 h-9 text-xs w-44" data-testid="fin-filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">
                <SelectItem value="all">All Categories</SelectItem>
                {(tab === 'income' ? incomeCategories : expenseCategories).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {tab === 'income' && (
              <Select value={fClient} onValueChange={setFClient}>
                <SelectTrigger className="bg-black/40 border-white/10 text-slate-300 h-9 text-xs w-44" data-testid="fin-filter-client"><SelectValue placeholder="All Clients" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="glass-card overflow-hidden" data-testid={`${tab}-table`}>
            <div className="overflow-x-auto mobile-cards">
            <table className="data-table">
              <thead><tr>
                <th>Date</th>
                <th>Description</th>
                {tab === 'income' && <th>Client</th>}
                {tab === 'expense' && <th>Vendor</th>}
                <th>Category</th>
                <th>Account</th>
                <th>Mode</th>
                <th>Amount</th>
                {tab === 'income' && <th>TDS</th>}
                {tab === 'income' && <th>Net</th>}
                {tab === 'expense' && <th>Receipt</th>}
                <th></th>
              </tr></thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr><td colSpan={tab === 'income' ? 9 : 8} className="text-center py-12 text-slate-500">No {tab} entries found</td></tr>
                ) : entries.map(e => (
                  <tr key={e.finance_id} data-testid={`fin-row-${e.finance_id}`}>
                    <td className="text-xs text-slate-400">{e.date}</td>
                    <td className="text-white text-sm">{e.description} {e.linked_invoice_id && <span className="text-[10px] text-indigo-400 ml-1">(Invoice)</span>}</td>
                    {tab === 'income' && <td className="text-slate-300 text-xs">{e.client_name || '—'}</td>}
                    {tab === 'expense' && <td className="text-slate-300 text-xs">{e.vendor_name || '—'}</td>}
                    <td><span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-slate-300">{e.category}</span></td>
                    <td><span className={`text-[10px] px-2 py-0.5 rounded border ${(e.account_type || 'Company') === 'Personal' ? 'bg-purple-500/10 text-purple-300 border-purple-500/20' : 'bg-indigo-500/10 text-indigo-300 border-indigo-500/20'}`}>{e.account_type || 'Company'}</span></td>
                    <td className="text-[10px] text-slate-400">{e.payment_mode || '—'}</td>
                    <td className={`font-mono text-sm font-bold ${tab === 'income' ? 'text-emerald-400' : 'text-rose-400'}`}>{formatCurrency(e.amount)}</td>
                    {tab === 'income' && <td className="text-xs text-amber-400">{e.tax_deduction ? formatCurrency(e.tax_deduction) : '—'}</td>}
                    {tab === 'income' && <td className="text-sm font-mono text-cyan-400">{formatCurrency(e.net_amount)}</td>}
                    {tab === 'expense' && <td>{e.receipt_link ? <a href={e.receipt_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-cyan-400 hover:text-cyan-300"><Receipt size={12} /></a> : <span className="text-slate-600 text-xs">—</span>}</td>}
                    <td>{!e.linked_invoice_id && <div className="flex gap-1"><button onClick={() => setEditEntry(e)} className="p-1 rounded hover:bg-indigo-500/10 text-indigo-400" data-testid={`edit-fin-${e.finance_id}`}><Edit2 size={12} /></button><button onClick={() => handleDelete(e.finance_id)} className="p-1 rounded hover:bg-rose-500/10 text-rose-400" data-testid={`delete-fin-${e.finance_id}`}><X size={12} /></button></div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Dialog */}
      {(showCreate || editEntry) && (
        <FinanceEntryDialog
          entryType={showCreate || editEntry?.entry_type}
          existingEntry={editEntry}
          categories={((showCreate || editEntry?.entry_type) === 'income') ? incomeCategories : expenseCategories}
          clients={clients}
          onClose={() => { setShowCreate(null); setEditEntry(null); }}
          onSaved={() => { setShowCreate(null); setEditEntry(null); fetchData(); }}
        />
      )}
    </SidebarLayout>
  );
}

// ========== ENTRY FORM ==========
function FinanceEntryDialog({ entryType, existingEntry, categories, clients, onClose, onSaved }) {
  const isIncome = entryType === 'income';
  const isEdit = !!existingEntry;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newCat, setNewCat] = useState('');
  const [localCats, setLocalCats] = useState(categories);

  const [form, setForm] = useState({
    date: existingEntry?.date || new Date().toISOString().split('T')[0],
    description: existingEntry?.description || '',
    client_id: existingEntry?.client_id || '',
    vendor_name: existingEntry?.vendor_name || '',
    category: existingEntry?.category || '',
    amount: existingEntry?.amount?.toString() || '',
    tax_deduction: existingEntry?.tax_deduction?.toString() || '',
    receipt_link: existingEntry?.receipt_link || '',
    reference: existingEntry?.reference || '',
    notes: existingEntry?.notes || '',
    payment_mode: existingEntry?.payment_mode || '',
    account_type: existingEntry?.account_type || 'Company',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await axios.post(`${API}/finance/categories`, { name: newCat.trim(), entry_type: entryType }, { withCredentials: true });
      setLocalCats(prev => [...prev, newCat.trim()].sort());
      set('category', newCat.trim());
      setNewCat('');
      toast.success('Category added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      const payload = {
        date: form.date, description: form.description,
        category: form.category, amount: parseFloat(form.amount) || 0,
        tax_deduction: parseFloat(form.tax_deduction) || 0,
        receipt_link: form.receipt_link || null,
        reference: form.reference || null, notes: form.notes || null,
        payment_mode: form.payment_mode || null,
        account_type: form.account_type || 'Company',
      };
      if (isIncome && form.client_id) payload.client_id = form.client_id;
      if (!isIncome && form.vendor_name) payload.vendor_name = form.vendor_name;
      if (isEdit) {
        await axios.put(`${API}/finance/${existingEntry.finance_id}`, payload, { withCredentials: true });
        toast.success(`${isIncome ? 'Income' : 'Expense'} updated`);
      } else {
        payload.entry_type = entryType;
        await axios.post(`${API}/finance`, payload, { withCredentials: true });
        toast.success(`${isIncome ? 'Income' : 'Expense'} recorded`);
      }
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#0a0a0f] border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white font-heading text-lg">{isEdit ? 'Edit' : 'Record'} {isIncome ? 'Income' : 'Expense'}</DialogTitle></DialogHeader>
        {error && <div className="p-2 rounded bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">{error}</div>}
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500 mb-1 block">Date *</label>
              <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" data-testid="fin-entry-date" /></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Amount *</label>
              <Input type="number" value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="0" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="fin-entry-amount" /></div>
          </div>
          <div><label className="text-xs text-slate-500 mb-1 block">Description *</label>
            <Input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder={isIncome ? 'e.g. Commission from Nike campaign' : 'e.g. Adobe Creative Suite yearly'} className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="fin-entry-desc" /></div>
          {isIncome && (
            <div><label className="text-xs text-slate-500 mb-1 block">Client</label>
              <Select value={form.client_id} onValueChange={(v) => set('client_id', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="fin-entry-client"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">
                  {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
          )}
          {!isIncome && (
            <div><label className="text-xs text-slate-500 mb-1 block">Vendor Name</label>
              <Input value={form.vendor_name} onChange={(e) => set('vendor_name', e.target.value)} placeholder="e.g. Adobe, AWS" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="fin-entry-vendor" /></div>
          )}
          <div><label className="text-xs text-slate-500 mb-1 block">Category *</label>
            <Select value={form.category} onValueChange={(v) => set('category', v)}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="fin-entry-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">
                {localCats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2 mt-2">
              <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category..." className="bg-black/40 border-white/10 text-white h-8 text-xs flex-1" data-testid="fin-new-cat" />
              <Button onClick={addCategory} variant="outline" className="h-8 text-xs bg-white/5 border-white/10 text-slate-300" data-testid="fin-add-cat"><Plus size={12} className="mr-1" />Add</Button>
            </div>
          </div>
          {isIncome && (
            <div><label className="text-xs text-slate-500 mb-1 block">Tax Deduction (TDS)</label>
              <Input type="number" value={form.tax_deduction} onChange={(e) => set('tax_deduction', e.target.value)} placeholder="0" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="fin-entry-tds" /></div>
          )}
          {!isIncome && (
            <div><label className="text-xs text-slate-500 mb-1 block">Receipt Link</label>
              <Input value={form.receipt_link} onChange={(e) => set('receipt_link', e.target.value)} placeholder="URL to receipt/invoice" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="fin-entry-receipt" /></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Mode of Payment</label>
              <Select value={form.payment_mode} onValueChange={(v) => set('payment_mode', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="fin-entry-payment-mode"><SelectValue placeholder="Select mode" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                  {['UPI', 'Bank Transfer', 'Cash', 'Cheque', 'Card', 'Other'].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Account Type *</label>
              <Select value={form.account_type} onValueChange={(v) => set('account_type', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="fin-entry-account-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                  <SelectItem value="Company">Company</SelectItem>
                  <SelectItem value="Personal">Personal (founder)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><label className="text-xs text-slate-500 mb-1 block">Reference</label>
            <Input value={form.reference} onChange={(e) => set('reference', e.target.value)} placeholder="Optional reference" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="fin-entry-ref" /></div>
          <div><label className="text-xs text-slate-500 mb-1 block">Notes</label>
            <Input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Optional" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="fin-entry-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button disabled={loading || !form.description || !form.amount || !form.category} onClick={handleSubmit}
            className={isIncome ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'} data-testid="submit-finance-entry">
            {loading ? 'Saving...' : isEdit ? `Update ${isIncome ? 'Income' : 'Expense'}` : `Record ${isIncome ? 'Income' : 'Expense'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
